import { FC, FormEventHandler, useEffect, useState } from "react";
import styles from "./add-work-cost.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import { useAddWorkCostMutation } from "../workCostApi";
import Checkbox from "../../../../../ui/Checkbox/Checkbox";
import { useGetAllPilotsQuery } from "../../pilots/pilotsApi";
import { useGetAllWorksCompletedQuery } from "../../worksCompleted/worksCompletedApi";
import { HHMM2min } from "../../../../../helpers/time";

type Props = {
  close: () => void;
  flight_work_id: number;
};

type State = {
  errors?: {
    work_id?: string[];
    worker_id?: string[];
    group_1?: string[];
    group_2?: string[];
    group_3?: string[];
    group_4?: string[];
    group_5?: string[];
    group_6?: string[];
    included?: string[];
  };
  message?: string | null;
};

const FormSchema = z.object({
  work_id: z.coerce
    .number({
      message: "Выберите из списка",
    })
    .positive({
      message: "Выберите из списка",
    }),
  worker_id: z.coerce
    .number({
      message: "Выберите из списка",
    })
    .positive({
      message: "Выберите из списка",
    }),
  group_1: z
    .string()
    .transform((val) => (val === "" ? null : HHMM2min(val)))
    .nullable(),
  group_2: z
    .string()
    .transform((val) => (val === "" ? null : HHMM2min(val)))
    .nullable(),
  group_3: z
    .string()
    .transform((val) => (val === "" ? null : HHMM2min(val)))
    .nullable(),
  group_4: z
    .string()
    .transform((val) => (val === "" ? null : HHMM2min(val)))
    .nullable(),
  group_5: z
    .string()
    .transform((val) => (val === "" ? null : HHMM2min(val)))
    .nullable(),
  group_6: z
    .string()
    .transform((val) => (val === "" ? null : HHMM2min(val)))
    .nullable(),
    included: z.coerce.boolean(),
});

const Form: FC<Props> = ({ close, flight_work_id }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [add, { isError, isLoading, isSuccess }] = useAddWorkCostMutation();
  const {
    data: pilots,
    isError: isPilotsError,
    isFetching: isPilotsFetching,
  } = useGetAllPilotsQuery(Number(flight_work_id));

  const {
    data: works,
    isError: isWorksError,
    isFetching: isWorksFetching,
  } = useGetAllWorksCompletedQuery(Number(flight_work_id));

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      work_id: formData.get("work_id"),
      worker_id: formData.get("worker_id"),
      group_1: formData.get("group_1"),
      group_2: formData.get("group_2"),
      group_3: formData.get("group_3"),
      group_4: formData.get("group_4"),
      group_5: formData.get("group_5"),
      group_6: formData.get("group_6"),
      included: formData.get("included"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }

    const {
      work_id,
      worker_id,
      group_1,
      group_2,
      group_3,
      group_4,
      group_5,
      group_6,
      included,
    } = validatedFields.data;

    add({
      flight_work_id,
      work_id,
      worker_id,
      group_1,
      group_2,
      group_3,
      group_4,
      group_5,
      group_6,
      included,
    });
  };

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Добавление группы оплаты</div>
      <div className={styles.row}>
        <label htmlFor="worker_id" className={styles.label}>
          Летчик-наблюдатель
        </label>
        <select className={styles.select} name="worker_id">
          {isPilotsFetching ? (
            <option>Загрузка списка...</option>
          ) : (
            <option className={styles.option}>Выберите</option>
          )}
          {pilots?.map((item) => (
            <option
              key={item.id}
              className={styles.option}
              value={item.worker_id}
            >
              {item.worker_name}
            </option>
          ))}
        </select>
        <div id="worker_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.worker_id &&
            state.errors.worker_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="work_id" className={styles.label}>
          Вид работ
        </label>
        <select className={styles.select} name="work_id">
          {isWorksFetching ? (
            <option>Загрузка списка...</option>
          ) : (
            <option className={styles.option}>Выберите</option>
          )}
          {works?.map((item) => (
            <option
              key={item.id}
              className={styles.option}
              value={item.work_id}
            >
              {item.contract_work_name}
            </option>
          ))}
        </select>
        <div id="work_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.work_id &&
            state.errors.work_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="group_1">Группа оплаты 1</label>
        <input
          className={styles.input}
          name="group_1"
          placeholder="Группа оплаты 1"
          type="time"
          defaultValue="00:00"
        />
        <div id="group_1-error" aria-live="polite" aria-atomic="true">
          {state.errors?.group_1 &&
            state.errors.group_1.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="group_2">Группа оплаты 2</label>
        <input
          className={styles.input}
          name="group_2"
          placeholder="Группа оплаты 2"
          type="time"
          defaultValue="00:00"
        />
        <div id="group_2-error" aria-live="polite" aria-atomic="true">
          {state.errors?.group_2 &&
            state.errors.group_2.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="group_3">Группа оплаты 3</label>
        <input
          className={styles.input}
          name="group_3"
          placeholder="Группа оплаты 3"
          type="time"
          defaultValue="00:00"
        />
        <div id="group_3-error" aria-live="polite" aria-atomic="true">
          {state.errors?.group_3 &&
            state.errors.group_3.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="group_4">Группа оплаты 4</label>
        <input
          className={styles.input}
          name="group_4"
          placeholder="Группа оплаты 4"
          type="time"
          defaultValue="00:00"
        />
        <div id="group_4-error" aria-live="polite" aria-atomic="true">
          {state.errors?.group_4 &&
            state.errors.group_4.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="group_5">Группа оплаты 5</label>
        <input
          className={styles.input}
          name="group_5"
          placeholder="Группа оплаты 5"
          type="time"
          defaultValue="00:00"
        />
        <div id="group_5-error" aria-live="polite" aria-atomic="true">
          {state.errors?.group_5 &&
            state.errors.group_5.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="group_6">Группа оплаты 6</label>
        <input
          className={styles.input}
          name="group_6"
          placeholder="Группа оплаты 6"
          type="time"
          defaultValue="00:00"
        />
        <div id="group_6-error" aria-live="polite" aria-atomic="true">
          {state.errors?.group_6 &&
            state.errors.group_6.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="included">Учитывать в ведомости оплаты</label>
        <div className={styles.checkbox}>
          <Checkbox name="included" />
        </div>
      </div>
      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {isPilotsError && (
        <Snackbar
          isOpen={isPilotsError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Не удалось загрузить список летчиков-наблюдателей
        </Snackbar>
      )}
    </form>
  );
};

export const AddWorkCost: FC<Props> = ({ close, flight_work_id }) => {
  return (
    <Modal close={close}>
      <Form close={close} flight_work_id={flight_work_id} />
    </Modal>
  );
};
